import { motion } from 'motion/react';
import { 
  Building2, 
  BarChart3, 
  Users2, 
  Settings2,  
  AlertCircle, 
  Globe,
  TrendingUp,
  ChevronRight
} from 'lucide-react';
import { cn } from '../../../utils/helpers';
import { Link } from 'react-router-dom';

export default function CompanyDashboard() {
  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">Company Dashboard</h1>
          <p className="text-neutral-500 dark:text-neutral-400">Manage your business profile, industry insights, and team.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link 
            to="/app/company/business/edit"
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors"
          >
            <Settings2 className="w-4 h-4" />
            Settings
          </Link>
          <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
            <Building2 className="w-4 h-4" />
            Business Profile
          </button>
        </div>
      </header>

      {/* Alerts / Notifications */}
      <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-100 rounded-2xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-900">Subscription Renewal</p>
          <p className="text-xs text-amber-700">Your Business Pro plan expires in 5 days. Renew now to avoid service interruption.</p>
        </div>
        <button className="ml-auto text-xs font-bold text-amber-900 hover:underline">Renew</button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'Market Share', value: '12.4%', change: '+1.2%', icon: Globe, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Team Members', value: '18', change: '+2', icon: Users2, color: 'text-purple-600 dark:text-purple-400' },
          { label: 'Revenue Growth', value: '24.5%', change: '+5.4%', icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Industry Rank', value: '#42', change: 'Top 5%', icon: BarChart3, color: 'text-amber-600 dark:text-amber-400' },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white dark:bg-neutral-900 p-6 rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn("p-2 rounded-xl bg-neutral-50 dark:bg-neutral-950", stat.color)}>
                <stat.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded-lg">
                {stat.change}
              </span>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 font-medium">{stat.label}</p>
            <h3 className="text-2xl font-bold mt-1">{stat.value}</h3>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-6">
          <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: 'Browse Industries', desc: 'Explore market trends and data', icon: Globe, path: '/app/company/industries' },
              { title: 'Market Assessment', desc: 'AI-driven industry matching', icon: BarChart3, path: '/app/company/assessment' },
              { title: 'Analytics Dashboard', desc: 'Deep dive into business KPIs', icon: TrendingUp, path: '/app/company/analytics' },
              { title: 'Company Chat', desc: 'Unified business messaging', icon: Users2, path: '/app/company/chat' },
            ].map((action) => (
              <Link
                key={action.title}
                to={action.path}
                className="group bg-white dark:bg-neutral-900 p-6 rounded-3xl border border-neutral-200 dark:border-neutral-800 hover:border-neutral-900 transition-all shadow-sm dark:shadow-none flex items-start gap-4"
              >
                <div className="p-3 rounded-2xl bg-neutral-50 dark:bg-neutral-950 group-hover:bg-neutral-900 group-hover:text-white transition-all">
                  <action.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-neutral-900 dark:text-neutral-50">{action.title}</h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{action.desc}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-neutral-300 group-hover:text-neutral-900 dark:group-hover:text-neutral-50 transition-colors mt-1" />
              </Link>
            ))}
          </div>

          {/* Recent Activity */}
          <div className="space-y-4 pt-4">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Recent Business Activity</h2>
            <div className="bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
              {[
                { title: 'New Partner Connected', desc: 'Global Logistics Inc. joined your network', time: '2h ago', icon: Users2 },
                { title: 'Report Generated', desc: 'Q3 Market Analysis is ready for review', time: '5h ago', icon: BarChart3 },
                { title: 'Plan Upgraded', desc: 'Your subscription was updated to Enterprise', time: '1d ago', icon: Building2 },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300">
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{item.title}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">{item.desc}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-widest">{item.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar: Current Plan */}
        <div className="space-y-6">
          <div className="bg-neutral-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden">
            <div className="relative z-10">
              <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-2">Current Plan</p>
              <h3 className="text-2xl font-bold mb-4">Business Pro</h3>
              <ul className="space-y-3 mb-8">
                {['Unlimited Team Members', 'Advanced Analytics', 'Priority Support', 'Custom Reports'].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-xs text-neutral-300">
                    <div className="w-1 h-1 bg-emerald-500 rounded-full"></div>
                    {feature}
                  </li>
                ))}
              </ul>
              <Link 
                to="/app/company/plans"
                className="block w-full bg-white text-neutral-900 py-3 rounded-xl font-bold text-center text-sm hover:bg-neutral-100 transition-colors"
              >
                Manage Subscription
              </Link>
            </div>
            <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-white/5 rounded-full blur-3xl"></div>
          </div>

          <div className="bg-white dark:bg-neutral-900 p-6 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none">
            <h3 className="font-bold mb-4">Industry Insights</h3>
            <div className="space-y-4">
              <div className="p-4 bg-neutral-50 dark:bg-neutral-950 rounded-2xl border border-neutral-100 dark:border-neutral-800">
                <p className="text-xs font-bold text-neutral-900 dark:text-neutral-50 mb-1">Tech Sector Trends</p>
                <p className="text-[10px] text-neutral-500 dark:text-neutral-400 leading-relaxed">AI adoption in small businesses is up 45% this quarter. Consider updating your strategy.</p>
              </div>
              <button className="w-full py-3 text-sm font-bold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">
                View All Insights
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
